import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";

// Déconnexion automatique après 30 min d'inactivité, avec avertissement (compte à rebours 60 s).
const IDLE_LIMIT_MS = 30 * 60 * 1000;   // 30 minutes sans activité
const WARN_BEFORE_MS = 60 * 1000;       // avertissement 60 s avant la déconnexion
const CHECK_INTERVAL_MS = 1000;

export default function InactivityGuard() {
  const { logout } = useAuth();
  const { t } = useApp();
  const navigate = useNavigate();
  const lastActivity = useRef(Date.now());
  const warningRef = useRef(false);
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(60);

  const doLogout = useCallback(() => {
    logout();
    toast.info(t("session_expired_inactive"));
    navigate("/login");
  }, [logout, navigate, t]);

  // Pendant l'avertissement, seule l'action explicite « Rester connecté » réinitialise le minuteur.
  const resetActivity = useCallback(() => {
    if (warningRef.current) return;
    lastActivity.current = Date.now();
  }, []);

  useEffect(() => {
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetActivity, { passive: true }));
    const id = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_LIMIT_MS) { doLogout(); return; }
      if (idle >= IDLE_LIMIT_MS - WARN_BEFORE_MS) {
        warningRef.current = true;
        setWarning(true);
        setRemaining(Math.max(0, Math.ceil((IDLE_LIMIT_MS - idle) / 1000)));
      }
    }, CHECK_INTERVAL_MS);
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetActivity));
      clearInterval(id);
    };
  }, [resetActivity, doLogout]);

  const stay = () => {
    warningRef.current = false;
    lastActivity.current = Date.now();
    setWarning(false);
  };

  if (!warning) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="inactivity-warning">
      <div className="bg-card text-foreground rounded-3xl w-full max-w-sm p-6 text-center">
        <div className="h-14 w-14 rounded-2xl bg-amber-500/15 text-amber-600 flex items-center justify-center mx-auto mb-4">
          <Clock className="h-7 w-7" />
        </div>
        <h2 className="font-bold text-lg mb-2">{t("session_expiring_title")}</h2>
        <p className="text-sm text-muted-foreground mb-1">{t("session_expiring_msg")}</p>
        <p className="text-4xl font-black tabular-nums my-3" data-testid="inactivity-countdown">{remaining}s</p>
        <div className="flex gap-2">
          <button onClick={stay} data-testid="inactivity-stay"
            className="flex-1 h-12 rounded-xl bg-brand text-white font-bold">
            {t("stay_connected")}
          </button>
          <button onClick={doLogout} data-testid="inactivity-logout"
            className="flex-1 h-12 rounded-xl bg-secondary text-secondary-foreground font-semibold">
            {t("logout")}
          </button>
        </div>
      </div>
    </div>
  );
}
